import { clientFamilyBreakdownSchema, clientListPageSchema, clientSchema, clientSheetSchema, createdClientSchema, lotSchema, } from '@ghp/domain';
import { z } from 'zod';
import { bearer } from './http.js';
import { queryString } from './query-string.js';
/**
 * Le répertoire « Clients & lots » (D21, D24). Le jeton voyage **en argument**, comme partout
 * ailleurs : celui de la session en cours, et rien d'autre.
 *
 * ⚠️ **L'import d'un lot n'y est toujours pas** : c'est un geste que la maquette montre et
 * qu'aucune route ne sert — le déclarer ici ferait croire à un câblage.
 */
export function createClientsEndpoints(http) {
    return {
        /**
         * Une page du répertoire — filtrée, triée et **comptée par le serveur**.
         *
         * ⚠️ `total` et `items.length` diffèrent dès la première page : c'est `total` qui dit « 12
         * clients » sous un tableau qui en montre cinquante au plus, et lui seul qui permet de savoir
         * s'il reste une page à demander.
         */
        list(accessToken, request = {}) {
            return http.get(`/clients${queryString(request)}`, clientListPageSchema, bearer(accessToken));
        },
        /**
         * La répartition du répertoire par famille, pour le tableau de bord — funnel compris
         * (`pipeline`).
         *
         * ⚠️ **Les trois familles arrivent toujours**, y compris à zéro : le contrat les exige, si
         * bien qu'une famille absente de la réponse est refusée ici plutôt que de se lire « aucun
         * client » à l'écran.
         */
        byFamily(accessToken) {
            return http.get(`/clients/by-family`, clientFamilyBreakdownSchema, bearer(accessToken));
        },
        /** Tous les secteurs, de tous les clients — le filtre « Secteur » de « Demandes ». */
        lots(accessToken) {
            return http.get('/clients/lots', z.array(lotSchema), bearer(accessToken));
        },
        /**
         * La fiche d'un client : son contact, son adresse, son avancement cumulé et l'historique de
         * ses lots — **en un seul appel**.
         *
         * ⚠️ L'avancement de chaque lot arrive **compté** (D24). L'écran ne descend donc pas les
         * demandes d'une campagne pour en additionner les étapes, ce qui ne passerait pas l'échelle
         * d'un bailleur à cinquante lots.
         */
        sheet(accessToken, id) {
            return http.get(`/clients/${id}`, clientSheetSchema, bearer(accessToken));
        },
        /**
         * Crée un donneur d'ordre, avec son contact et son adresse.
         *
         * ⚠️ **`duplicateNames` n'est pas un refus** : le nom n'a aucune contrainte d'unicité, et deux
         * communes homonymes existent. Le client est créé ; ce compte est ce que l'écran affiche pour
         * que le gestionnaire tranche lui-même (M21).
         */
        create(accessToken, input) {
            return http.postJson('/clients', input, createdClientSchema, bearer(accessToken));
        },
        /**
         * Les clients qui portent déjà ce nom, aux accents, à la casse et aux espaces près — à lire
         * **avant** `create`, pour proposer l'existant plutôt qu'un doublon (#653).
         */
        homonyms(accessToken, name) {
            return http.get(`/clients/homonyms?name=${encodeURIComponent(name)}`, z.array(clientSchema), bearer(accessToken));
        },
        /**
         * Archive un **particulier** : il quitte le répertoire actif (`archived=false`, le défaut de
         * `list`) sans que rien soit supprimé. Rend le client tel qu'il est désormais.
         *
         * ⚠️ **`409` à afficher mot pour mot** pour toute autre catégorie — une commune ou un bailleur
         * ne s'archive pas. Idempotent : déjà archivé, il garde sa date.
         */
        archive(accessToken, id) {
            return http.postJson(`/clients/${id}/archive`, {}, clientSchema, bearer(accessToken));
        },
        /** Le rend au répertoire actif — même garde, même réponse. */
        unarchive(accessToken, id) {
            return http.postJson(`/clients/${id}/unarchive`, {}, clientSchema, bearer(accessToken));
        },
        /** Supprime un secteur vide du client. ⚠️ `409` à afficher mot pour mot s'il porte des adresses. */
        deleteLot(accessToken, id, lotId) {
            return http.deleteEmpty(`/clients/${id}/lots/${lotId}`, bearer(accessToken));
        },
        /** Supprime les demandes d'un secteur — admin seul. ⚠️ `409` à afficher mot pour mot. */
        deleteLotInquiries(accessToken, id, lotId) {
            return http.deleteEmpty(`/clients/${id}/lots/${lotId}/inquiries`, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=clients.js.map